/* ============================================================================
   FitLibrary — fitlibrary-review-submit.js
   THE SUBMISSION BACKEND. Collection Mode.

   This file makes the Write a Review form real. It registers a WRITE provider
   with the shared source and nothing else: it reads no reviews, renders no
   markup, and cannot affect a single card on the page.

   That separation is the whole point of Collection Mode. The storefront keeps
   displaying the sixty-seven sample reviews; a real submission goes to the
   production backend and is stored there, unpublished, as a review bank for a
   later migration. Read and write are two different questions and this file
   only answers the second one — see MODE in assets/fitlibrary-review-source.js
   for the first.

   THE FIRST-PARTY PATH, NOT THE WORKER'S OWN HOSTNAME.
   The endpoint is the relative path /apps/reviews/submit. It is served by the
   Shopify App Proxy on the shop's own domain, which is what makes this work
   and what makes it safe:

     · Shopify signs every proxied request before it reaches the Worker, so the
       Worker can prove the request came through this shop and reject anything
       that did not. A browser cannot forge that signature and neither can
       this file — it does not hold the secret and never will.
     · Same-origin, so there is no CORS preflight, no third-party cookie
       question, and no third-party hostname in the page for anyone to hit
       directly.
     · The service's own hostname stays an implementation detail that can
       change without touching the theme.

   NO CREDENTIALS LIVE HERE. There is no token, no app secret, no Admin API
   endpoint and no shop identifier in this file. Everything privileged happens
   server-side, which is exactly why the storefront is allowed to post at all.

   WHAT IT NEVER DOES. It does not add the submitted review to any carousel,
   change any average or count, refresh the sample data, or tell the customer
   their review is live. A submission has no effect on what this page displays,
   because the review it created is a DRAFT that no public read can see.
   ============================================================================ */

(function (window, document) {
  "use strict";

  var SRC = window.FitLibraryReviews;
  if (!SRC || typeof SRC.setWriteProvider !== "function") return;

  /* Relative on purpose. See the header: this is the App Proxy path on the
     shop's own domain, and it is the only endpoint this file knows. */
  var SUBMIT_PATH = "/apps/reviews/submit";

  var TIMEOUT_MS = 20000;

  /* An idempotency key travels with the request so that a retry — a flaky
     connection, an impatient second tap — cannot create a second review. The
     Worker keys on it in two places, so a repeat returns the first answer
     instead of writing again. */
  function idempotencyKey() {
    try {
      if (window.crypto && window.crypto.randomUUID) return window.crypto.randomUUID();
    } catch (e) {}
    return "fl-" + Date.now().toString(36) + "-" + Math.random().toString(36).slice(2, 10);
  }

  /* The customer never sees a server's own words. The Worker returns a short
     public message and keeps its detail in its logs; anything unexpected gets
     this generic line rather than a status code or a stack. */
  var GENERIC = "Your review could not be submitted. Please try again in a moment.";

  function messageFor(status, data) {
    if (data && typeof data.error === "string" && data.error) return data.error;
    if (status === 429) return "You have submitted several reviews recently. Please try again later.";
    if (status === 401 || status === 403) return GENERIC;
    return GENERIC;
  }

  var provider = {
    name: "app-proxy",

    /* True because this is a real, persistent, server-side store. It says
       nothing about what the storefront DISPLAYS — that is MODE.display, and
       it is "sample_only". The two were the same flag once and are not any
       more, which is what lets the form be live while the cards are samples. */
    isPersistent: true,

    /* Reading is not this provider's job. It is registered through
       setWriteProvider, so the source never asks it for reviews or products;
       these exist so that a mistaken setProvider() call fails loudly instead
       of returning an empty store that looks like "no reviews yet". */
    getProducts: function () {
      throw new Error("fitlibrary-review-submit: this is a write-only provider.");
    },
    getReviews: function () {
      throw new Error("fitlibrary-review-submit: this is a write-only provider.");
    },

    submitReview: function (record) {
      /* `record` is what the shared source built and normalized: product
         handle and id, rating, title, body, display name, email, consent,
         source, and `age` ONLY when the reviewer gave one. Nothing is added
         here — in particular there is no order number, in any spelling. The
         field was retired from the form and from this pipeline together. */
      var payload = {
        productId: record.productId,
        productHandle: record.productHandle,
        rating: record.rating,
        title: record.title,
        body: record.body,
        reviewerDisplayName: record.reviewerDisplayName,
        email: record.email,
        consent: record.consent === true
      };
      if (record.age !== undefined && record.age !== null && String(record.age) !== "") {
        payload.age = record.age;
      }

      var controller = null;
      var timer = 0;
      try {
        controller = new AbortController();
        timer = window.setTimeout(function () { controller.abort(); }, TIMEOUT_MS);
      } catch (e) { controller = null; }

      var options = {
        method: "POST",
        headers: {
          "content-type": "application/json",
          "accept": "application/json",
          "idempotency-key": idempotencyKey()
        },
        body: JSON.stringify(payload),
        /* Same-origin through the App Proxy. Shopify needs the request to
           carry its normal context to sign it. */
        credentials: "same-origin"
      };
      if (controller) options.signal = controller.signal;

      return window.fetch(SUBMIT_PATH, options)
        .then(function (res) {
          return res.text().then(function (text) {
            var data = null;
            try { data = text ? JSON.parse(text) : null; } catch (e) { data = null; }
            return { status: res.status, ok: res.ok, data: data };
          });
        })
        .then(function (r) {
          if (timer) window.clearTimeout(timer);

          if (!r.ok || !r.data || r.data.ok !== true) {
            throw new Error(messageFor(r.status, r.data));
          }

          /* THE ONLY HONEST ANSWER IS "PENDING".

             The Worker created an unpublished DRAFT. Nothing published it,
             nothing will publish it on a timer, and no rating makes any
             difference. The status is passed straight through rather than
             translated into anything more encouraging, so no caller can end up
             telling a customer their review is live. */
          return {
            ok: true,
            status: r.data.status || "pending",
            reviewId: r.data.reviewId || ""
          };
        })
        .catch(function (err) {
          if (timer) window.clearTimeout(timer);
          if (err && err.name === "AbortError") {
            throw new Error("That took too long. Please check your connection and try again.");
          }
          /* A thrown Error from the block above already carries a customer-safe
             message. A network failure does not, so it gets the generic one. */
          throw new Error(err && err.message ? err.message : GENERIC);
        });
    }
  };

  SRC.setWriteProvider(provider);
})(window, document);
